class EngieWizard {
	/**
	 * Constructor
	 * @param {HTMLElement} wrapper Container formular
	 * @param {Object} [options = {}] Optiuni aditionale pentru configurare wizard
	 * @param {Boolean} [options.devMode = false] Daca e true, validarea individuala pe pasi e ignorata. Default false
	 * @param {Number} [options.initialStep = 0] Pasul initial. Ignorat daca devMode === false. Default 0
	 * @param {Boolean} [options.scrollOnStepChange = true] Daca e true, se face scroll la wrapper la fiecare schimbare de pas. Default true
	 * @param {Boolean} [options.scrollOnReset = false] Daca e true, se face scroll la wrapper la resetare wizard. Default false
	 * @param {Number} [options.extraOffset = 25] Offset suplimentar pentru scroll (pixeli). Inaltimea headerului e calculata automat. Default 25
	 * @param {HTMLElement} [options.startBtn = null] Buton trigger start wizard
	 * @param {HTMLElement} [options.resetBtn = null] Buton reset wizard
	 */
	constructor(wrapper, options = {}) {
		if (!wrapper) {
			throw new Error('wrapper e undefined');
		}

		try {
			this.events = JSON.parse(wrapper.dataset.events || '{}');
		} catch (err) {
			console.error('Eroare la parsarea evenimentelor');
			throw new Error(err.message);
		}

		const mergedOptions = {
			...{
				devMode: false,
				initialStep: 0,
				scrollOnStepChange: true,
				scrollOnReset: false,
				extraOffset: 25,
			},
			...options,
		};

		const { devMode, initialStep, scrollOnStepChange, scrollOnReset, extraOffset, startBtn, resetBtn } = mergedOptions;

		this.devMode = devMode;
		this.initialStep = devMode ? initialStep : 0;
		this.scrollOnStepChange = scrollOnStepChange;
		this.scrollOnReset = scrollOnReset;
		this.extraOffset = extraOffset;

		this.wrapper = wrapper;

		this.emit('beforeInit');

		this.frontStep = wrapper.querySelector('.front-step');
		this.endStep = wrapper.querySelector('.end-step');
		this.wizardInner = wrapper.querySelector('.wizard-inner:not(.front-step):not(.end-step)');
		this.startBtn = startBtn;
		this.resetBtn = resetBtn;

		if (!this.wizardInner) {
			throw new Error('Nu am gasit nicio sectiune .wizard-inner');
		}

		this.nav = {
			prevBtns: wrapper.querySelectorAll('button.prev'),
			nextBtns: wrapper.querySelectorAll('button.next'),
		};
		this.progress = {
			current: wrapper.querySelector('.current'),
			total: wrapper.querySelector('.total'),
			page: wrapper.querySelector('.step-progress'),
		};
		this.steps = wrapper.querySelectorAll('.wizard-step');
		this.stepTitle = wrapper.querySelector('.step-title');
		this.currentStep = this.devMode && this.initialStep ? this.initialStep : 0;
		this.totalSteps = this.steps.length;
		this.canProgress = Array(this.totalSteps).fill(false);
		this.feedback = wrapper.querySelector('.wizard-feedback');
		this.tabs = wrapper.querySelectorAll('[role="tab"]');
		this.tabPanels = wrapper.querySelectorAll('[role="tabpanel"]');

		this.init();

		this.emit('afterInit');

		this.updateStep();
		this.addListeners();

		// Inregistrare globala instanta
		window.engieWizards = window.engieWizards || {};
		if (!window.engieWizards[wrapper.id]) {
			window.engieWizards[wrapper.id] = this;
		}

		// Functie retrieve instanta
		if (!window.engieWizards.getInstance) {
			window.engieWizards.getInstance = function (elm) {
				if (typeof elm === 'string') {
					return window.engieWizards[elm] || {};
				}

				if (elm instanceof HTMLElement) {
					return Object.values(window.engieWizards).filter((v) => v.wrapper === elm)[0] || {};
				}

				return {};
			};
		}
	}

	init() {
		// Progres
		if (this.progress.page) {
			for (let i = 0; i < this.totalSteps; i++) {
				const indicator = document.createElement('span');
				indicator.classList.add('progress');
				this.progress.page.appendChild(indicator);
			}

			this.progress.indicators = this.wrapper.querySelectorAll('.progress');
		}

		if (this.progress.total) {
			this.progress.total.textContent = this.totalSteps;
		}

		this.validateCurrentStep();
	}

	addListeners() {
		// Listener reset
		window.addEventListener('engieWizard:reset', (e) => {
			this.reset();
		});

		// Butoane prev si next
		this.nav.prevBtns?.forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.preventDefault();
				this.prev();
			});
		});
		this.nav.nextBtns?.forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.preventDefault();
				this.next();
			});
		});

		// Front step si trigger wizard
		this.startBtn?.addEventListener('click', (e) => {
			e.preventDefault();
			this.frontStep?.setAttribute('hidden', 'hidden');
			this.frontStep?.setAttribute('aria-hidden', 'true');
			this.wizardInner.hidden = false;
			this.wizardInner.setAttribute('aria-hidden', 'false');
		});

		// End step, trigger reset
		this.resetBtn?.addEventListener('click', (e) => {
			e.preventDefault();
			this.reset();

			if (this.frontStep) {
				this.frontStep.hidden = false;
				this.frontStep.setAttribute('aria-hidden', 'false');
				this.wizardInner.hidden = true;
				this.wizardInner.setAttribute('aria-hidden', 'true');
			} else {
				this.wizardInner.hidden = false;
				this.wizardInner.setAttribute('aria-hidden', 'false');
			}

			this.endStep?.setAttribute('hidden', 'hidden');
			this.endStep?.setAttribute('aria-hidden', 'true');
		});

		// Triggere individuale pasi
		for (const [stepIndex, step] of [...this.steps].entries()) {
			const selector = step.dataset.progressTrigger;
			if (!selector) {
				continue;
			}

			const stepTriggers = step.querySelectorAll(selector);
			for (const stepTrigger of stepTriggers) {
				stepTrigger.addEventListener('click', (e) => {
					e.preventDefault();
					this.canProgress[stepIndex] = true;
					this.next();
				});
			}
		}

		// Validare pasi - debounced ca sa nu se valideze de mai multe ori
		let validationTimeout;

		const triggerValidation = () => {
			clearTimeout(validationTimeout);
			validationTimeout = setTimeout(() => this.validateCurrentStep(), 50);
		};

		this.wrapper.addEventListener('change', triggerValidation);
		this.wrapper.addEventListener('input', triggerValidation);

		// Activare content
		for (const tab of this.tabs) {
			tab.addEventListener('click', (e) => {
				e.preventDefault();
				const tabPanel = document.getElementById(tab.getAttribute('aria-controls'));
				tab.setAttribute('aria-expanded', 'true');
				[...this.tabs].filter((t) => t !== tab).forEach((t) => t.setAttribute('aria-expanded', 'false'));
				tabPanel.hidden = false;
				tabPanel.setAttribute('aria-hidden', 'false');
				[...this.tabPanels]
					.filter((t) => t !== tabPanel)
					.forEach((t) => {
						t.hidden = true;
						t.setAttribute('aria-hidden', 'true');
					});
			});
		}

		// Butoane feedback
		if (this.feedback) {
			const buttons = this.feedback.querySelectorAll('button');
			const message = this.feedback.dataset.thankyouMessage || 'Îți mulțumim pentru răspuns!';
			const thankYouMessage = `
				<div class="d-flex nj-gap-8 align-items-center">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#0b2d4e" stroke-width="1.5" opacity=".5"/><path stroke="#0b2d4e" stroke-linecap="round" stroke-width="1.5" d="M9 16c.85.63 1.885 1 3 1s2.15-.37 3-1"/><path fill="#0b2d4e" d="M16 10.5c0 .828-.448 1.5-1 1.5s-1-.672-1-1.5.448-1.5 1-1.5 1 .672 1 1.5"/><ellipse cx="9" cy="10.5" fill="#0b2d4e" rx="1" ry="1.5"/></svg>
					<p class="m-0">${message}</p>
				</div>
				`;

			for (const button of buttons) {
				button.addEventListener('click', (e) => {
					e.preventDefault();
					this.feedback.innerHTML = thankYouMessage;

					setTimeout(
						() => {
							this.feedback.remove();
							this.feedback = null;
						},
						Number(this.feedback.dataset.timeout || 2500),
					);
				});
			}
		}
	}

	reset(elements = {}) {
		this.currentStep = 0;
		this.canProgress.fill(false);

		for (const step of this.steps) {
			const selector = step.dataset.progressTrigger;
			if (!selector) {
				continue;
			}

			const stepTriggers = step.querySelectorAll(selector);
			for (const stepTrigger of stepTriggers) {
				stepTrigger.classList.remove('selected');
			}
		}

		this.updateStep();
		this.validateCurrentStep();

		// Scroll la wizard
		if (this.scrollOnReset) {
			this.scrollToWrapper();
		}

		// Ascundere elemente
		const { hideElements, unselectElements } = elements;

		if (hideElements && Array.isArray(hideElements)) {
			for (const selector of hideElements) {
				for (const elm of this.wrapper.querySelectorAll(selector)) {
					elm.hidden = true;
					elm.setAttribute('aria-hidden', 'true');
				}
			}
		}

		if (unselectElements && Array.isArray(unselectElements)) {
			for (const selector of unselectElements) {
				for (const elm of this.wrapper.querySelectorAll(selector)) {
					elm.classList.remove('selected');
					elm.setAttribute('aria-expanded', 'false');
				}
			}
		}
	}

	validateCurrentStep() {
		const currentStepElement = this.steps.length && this.steps[this.currentStep];
		if (!currentStepElement) {
			return;
		}

		// Verificare conditii explicite pas
		let stepValidation = false;
		const rawValidation = currentStepElement.dataset.stepValidation;

		try {
			if (rawValidation) {
				stepValidation = JSON.parse(rawValidation);
			}
		} catch (err) {
			stepValidation = false;
			console.warn('JSON invalid in data-step-validation:', currentStepElement);
			console.error('JSON invalid in data-step-validation:', err.message);
		}

		// Verificare validare explicita pas
		let stepValid = true;

		if (stepValidation !== null && typeof stepValidation === 'object') {
			const keys = Object.keys(stepValidation);
			keys.forEach((key) => {
				// Verificam daca avem de testat inegalitate
				const isInequality = key.endsWith('!');
				const name = isInequality ? key.slice(0, -1) : key;

				const selector = `[name="${name}"]`;
				const inputType = this.wrapper.querySelector(selector)?.type;

				if (inputType) {
					const value = inputType === 'radio' || inputType === 'checkbox' ? this.wrapper.querySelector(`${selector}:checked`)?.value : this.wrapper.querySelector(selector)?.value;
					const expectedValue = stepValidation[key];

					if (isInequality) {
						// Pentru inegalitati (ex. 'name!'), invalidam daca valoarea ESTE EGALA cu valoare asteptata
						if (value === expectedValue) {
							stepValid = false;
						}
					} else {
						// Pentru egalitati ('name'), invalidam daca valoarea NU ESTE EGALA cu valoarea asteptata
						if (value !== expectedValue) {
							stepValid = false;
						}
					}
				}
			});
		}

		// Elementele din pasul activ
		const inputs = [...currentStepElement.querySelectorAll('input, select, textarea')];

		// Daca unul din elemente nu e valid, pasul nu e valid
		const isValid = this.devMode ? true : inputs.length > 0 ? inputs.every((i) => i.checkValidity()) : true;

		this.canProgress[this.currentStep] = isValid && stepValid;
		this.updateNav();

		this.emit('validateStep', {
			currentStep: this.currentStep + 1,
			totalSteps: this.totalSteps,
			isValid: isValid && stepValid,
		});
	}

	updateNav() {
		// Butoane inapoi
		this.nav.prevBtns?.forEach((btn) => {
			const isFirstStep = this.currentStep === 0;
			const isDisabled = isFirstStep;

			btn.disabled = isDisabled;
			btn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

			if (btn.classList.contains('footer-nav')) {
				btn.hidden = isFirstStep;
				btn.setAttribute('aria-hidden', isFirstStep ? 'true' : 'false');
			}
		});

		// Butoane inainte
		this.nav.nextBtns?.forEach((btn) => {
			const isLastStep = this.currentStep === this.totalSteps - 1;
			const isDisabled = !this.canProgress[this.currentStep] || isLastStep;

			btn.disabled = isDisabled;
			btn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

			if (btn.classList.contains('footer-nav')) {
				btn.hidden = isLastStep;
				btn.setAttribute('aria-hidden', isLastStep ? 'true' : 'false');
			}
		});
	}

	updateStep() {
		// Pasul curent
		if (this.progress.current) {
			this.progress.current.textContent = this.currentStep + 1;
		}

		// Progres
		this.progress.indicators?.forEach((indicator, index) => {
			indicator.classList.toggle('completed', index <= this.currentStep);
		});

		// Titlu pas
		if (this.stepTitle) {
			this.stepTitle.textContent = this.stepTitles[this.currentStep];
		}

		// Continut pas
		if (this.steps.length > 0) {
			this.steps[this.currentStep].hidden = false;
			this.steps[this.currentStep].setAttribute('aria-hidden', 'false');
			[...this.steps]
				.filter((s) => s !== this.steps[this.currentStep])
				.forEach((s) => {
					s.hidden = true;
					s.setAttribute('aria-hidden', 'true');
				});
		}

		// Feedback
		if (this.feedback) {
			this.feedback.hidden = this.currentStep !== this.totalSteps - 1;
		}

		// Scroll la wrapper daca nu suntem in pasul 0
		if (this.scrollOnStepChange && this.currentStep > 0) {
			this.scrollToWrapper();
		}
	}

	next() {
		if (!this.canProgress[this.currentStep] || this.currentStep === this.totalSteps - 1) {
			return;
		}

		this.currentStep++;

		this.updateStep();
		this.validateCurrentStep();

		this.emit('updateStep', {
			currentStep: this.currentStep + 1,
			totalSteps: this.totalSteps,
			direction: 'next',
		});

		this.emit('next', {
			currentStep: this.currentStep + 1,
			totalSteps: this.totalSteps,
		});
	}

	prev() {
		if (this.currentStep === 0) {
			return;
		}

		this.currentStep--;

		this.updateStep();
		this.validateCurrentStep();

		this.emit('updateStep', {
			currentStep: this.currentStep + 1,
			totalSteps: this.totalSteps,
			direction: 'prev',
		});

		this.emit('prev', {
			currentStep: this.currentStep + 1,
			totalSteps: this.totalSteps,
		});
	}

	emit(event, detail = {}) {
		this.wrapper?.dispatchEvent(
			new CustomEvent(`engieWizard:${event}`, {
				bubbles: true,
				detail: {
					...detail,
					wrapper: this.wrapper,
				},
			}),
		);
	}

	scrollToWrapper() {
		// Calcul scroll-margin-top
		const header = document.getElementById('masthead');
		const headerHeight = header ? header.offsetHeight : 0;

		this.wrapper.style.scrollMarginTop = `${headerHeight + this.extraOffset}px`;
		this.wrapper.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	}

	get stepTitles() {
		try {
			const stepTitles = JSON.parse(this.wrapper.dataset.stepTitles);
			return Array.isArray(stepTitles) && stepTitles.length ? stepTitles : [];
		} catch (err) {
			console.error('Nu am putut extrage titlurile pasilor.');
			throw new Error(err.message);
		}
	}
}

export default EngieWizard;
